import { X } from "lucide-react";
import { useEffect, useId, useRef, useState, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";

type ResponsiveSheetProps = {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  triggerRef?: RefObject<HTMLElement | null>;
  layout?: "drawer" | "modal";
};

const focusableSelector = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

export function ResponsiveSheet({ open, title, onClose, children, footer, triggerRef, layout = "drawer" }: ResponsiveSheetProps) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  const openerRef = useRef<HTMLElement | null>(null);
  const [rendered, setRendered] = useState(open);
  const [state, setState] = useState<"open" | "closing">("open");

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (open) {
      setRendered(true);
      setState("open");
      return;
    }
    if (!rendered) return;
    setState("closing");
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    const timer = window.setTimeout(() => setRendered(false), reducedMotion ? 0 : 220);
    return () => window.clearTimeout(timer);
  }, [open, rendered]);

  useEffect(() => {
    if (!open) return;
    const scrollPosition = window.scrollY;
    const previousOverflow = document.body.style.overflow;
    openerRef.current = triggerRef?.current ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus({ preventScroll: true });

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(focusableSelector));
      if (!focusable.length) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (window.scrollY !== scrollPosition) window.scrollTo(0, scrollPosition);
      openerRef.current?.focus({ preventScroll: true });
    };
  }, [open, triggerRef]);

  if (!open && !rendered) return null;

  return createPortal(
    <div className={`ui-sheet-layer ui-sheet-layer--${layout}`} data-state={state} aria-hidden={state === "closing" ? true : undefined}>
      <button className="ui-sheet-scrim" type="button" aria-label={`Close ${title}`} onClick={() => onCloseRef.current()} />
      <div
        className={`ui-sheet ui-sheet--${layout} mobile-safe-bottom`}
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <header className="ui-sheet__header">
          <h2 id={titleId}>{title}</h2>
          <button className="ui-icon-button" type="button" aria-label={`Close ${title}`} onClick={() => onCloseRef.current()}>
            <X aria-hidden="true" size={20} />
          </button>
        </header>
        <div className="ui-sheet__body">{children}</div>
        {footer ? <footer className="ui-sheet__footer">{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}
